import { ZoomTransform, select, zoom, zoomIdentity } from 'd3'
import { DependencyList, useEffect, useRef, useState } from 'react'

interface ZoomPanOptions {
  scaleExtent: [number, number]
  // The transform the view opens with, applied once; undefined while there is
  // nothing to fit yet
  fit(): ZoomTransform | undefined
  // Events the zoom must leave alone, such as a press on a draggable node
  filter?(event: Event): boolean
}

// Zoom and pan of a view drawn by its own component (#107: Hierarchy, Links),
// the circles engine having its own. Gives the element to listen on and the
// transform to apply to the drawing. The zoom is rebuilt when deps change
export function useZoomPan(options: ZoomPanOptions, deps: DependencyList) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity)
  const fitted = useRef(false)
  // The latest options, read when the zoom is rebuilt
  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const { scaleExtent, fit, filter } = optionsRef.current
    const behavior = zoom<HTMLDivElement, unknown>()
      .scaleExtent(scaleExtent)
      .on('zoom', (event) => setTransform(event.transform))
    if (filter) behavior.filter(filter)
    const selection = select(container).call(behavior)
    if (!fitted.current) {
      const initial = fit()
      if (initial) {
        fitted.current = true
        selection.call(behavior.transform, initial)
      }
    }
    return () => {
      selection.on('.zoom', null)
    }
  }, deps)

  return { containerRef, transform }
}
